// components/BluetoothConnect.tsx
import React, { useEffect, useRef, useState } from 'react';
import './BluetoothConnect.css';

// ---- 최소 타입 선언: TS에서 Web Bluetooth 타입이 없을 때 에러 방지 ----
declare global {
  interface Navigator {
    bluetooth?: any;
  }
}
type BluetoothDevice = any;
type BluetoothRemoteGATTServer = any;
type BluetoothRemoteGATTCharacteristic = any;
// -------------------------------------------------------------------------

const SERVICE_UUID = '4fafc201-1fb5-459e-8fcc-c5c9c331914b';
const CHARACTERISTIC_UUID = 'beb5483e-36e1-4688-b7f5-ea07361b26a8';

const BluetoothConnect: React.FC = () => {
  const [supported, setSupported] = useState<boolean>(false);
  const [device, setDevice] = useState<BluetoothDevice | null>(null);
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [characteristic, setCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [status, setStatus] = useState<string>('대기 중');
  const [isSent, setIsSent] = useState<boolean>(false);

  // Wi-Fi/MQTT 전송값
  const [ssid, setSsid] = useState<string>(() => localStorage.getItem('bt_ssid') || '');
  const [pw, setPw] = useState<string>(() => localStorage.getItem('bt_pw') || '');
  const [email, setEmail] = useState<string>(() => localStorage.getItem('bt_email') || '');
  const [broker, setBroker] = useState<string>(() => localStorage.getItem('bt_broker') || 'mqtt.i2r.link');

  useEffect(() => {
    localStorage.setItem('bt_ssid', ssid);
    localStorage.setItem('bt_pw', pw);
    localStorage.setItem('bt_email', email);
    localStorage.setItem('bt_broker', broker);
  }, [ssid, pw, email, broker]);

  useEffect(() => {
    setSupported(!!navigator.bluetooth);
  }, []);

  const [isSending, setIsSending] = useState<boolean>(false);

  const connect = async () => {
    setIsSent(false);
    if (!navigator.bluetooth) {
      setSupported(false);
      return;
    }
    try {
      setStatus('기기 검색 중…');
      const dev: BluetoothDevice = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'i2r' }],
        optionalServices: [SERVICE_UUID],
      });
      setDevice(dev);

      dev.addEventListener?.('gattserverdisconnected', handleDisconnected);

      setStatus('기기 연결 중…');
      const gatt: BluetoothRemoteGATTServer | undefined = await dev.gatt?.connect();
      if (!gatt) throw new Error('GATT 연결 실패');
      setServer(gatt);

      const service = await gatt.getPrimaryService(SERVICE_UUID);
      const ch = await service.getCharacteristic(CHARACTERISTIC_UUID);
      setCharacteristic(ch);

      // 연결 성공 즉시 Wi-Fi / MQTT 설정 전송
      setStatus('설정 전송 중...');
      setIsSending(true);
      const payload = {
        c: 'si',
        ssid: ssid.trim(),
        password: pw.trim(),
        e: email.trim(),
        mqttBroker: broker.trim() || 'i2r',
      };

      const enc = new TextEncoder().encode(JSON.stringify(payload));
      await ch.writeValue(enc);
      setIsSent(true);
      setStatus('전송 완료 (연결 완료)');

    } catch (err) {
      setStatus('에러 또는 취소됨');
    } finally {
      setIsSending(false);
    }
  };

  const handleDisconnected = () => {
    setStatus('대기 중');
    setServer(null);
    setCharacteristic(null);
    setIsSent(false);
    setDevice(null);
  };

  return (
    <div className="bt-wrap">
      <h2 className="bt-title">기기연결 (Bluetooth)</h2>

      {/* 안내문 추가 */}
      <p className="bt-info">
        블루투스 모듈이 있어야 하며, 크롬 브라우저를 사용하세요.<br />
        안드로이드와 맥북은 동작하지만 아이폰은 연결되지 않습니다.
      </p>

      {!supported && (
        <p className="bt-warn">
          이 브라우저는 Web Bluetooth를 지원하지 않습니다. (HTTPS, Chrome/Edge/Android 권장)
        </p>
      )}

      <div className="bt-status"><b>상태:</b> {status}</div>

      <div className="bt-row">
        <button className="bt-btn" onClick={connect} disabled={!supported || isSending}>
          {isSending ? '처리 중...' : 'i2r* 기기 검색 / 자동 연결'}
        </button>
      </div>

      <fieldset className="bt-fieldset">
        <legend className="bt-legend">Wi-Fi / MQTT 설정 전송</legend>

        <div className="bt-grid">
          <label>SSID</label>
          <input className="bt-input" name="iot_ssid" type="text" value={ssid} onChange={(e) => setSsid(e.target.value)} placeholder="SSID" autoComplete="off" />

          <label>PW</label>
          <input className="bt-input" name="iot_pw" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoComplete="off" />

          <label>Email</label>
          <input className="bt-input" name="iot_email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="email" />

          <label>Broker</label>
          <input className="bt-input" name="iot_broker" type="text" value={broker} onChange={(e) => setBroker(e.target.value)} placeholder="mqtt.i2r.link" autoComplete="nope" />
        </div>

      </fieldset>
    </div>
  );
};

export default BluetoothConnect;
